import Tooltip from '@material-ui/core/Tooltip';
import React, { useEffect, useState } from 'react';

import { fetchMaterialUnitType } from '@services/commonService';

import {
	categoryTypeSustainabilityForTrimsAndBranding,
	getConsumptionUnit,
	MATERIAL_TITLE_NAME,
	someDetailsForTrimsAndBrandingRender,
	STATUS
} from '@utils/MaterialsUtil';
import { addImageSuffix, capitalizeFirstLetter } from '@utils/Util';

const TechPackMaterialSection = ({ productMaterials = {}, products = {} }) => {
	const [allUnitTypes, setAllUnitTypes] = useState([]);

	useEffect(() => {
		(async () => {
			const unitTypes = await fetchMaterialUnitType();
			setAllUnitTypes(unitTypes);
		})();
	}, []);

	const returnMaterialType = (type) => {
		if (type === 'BRANDING') {
			return 'ACCESSORIES';
		} else {
			return type;
		}
	};

	const materialDetailsRender = (title, objectInfo) => {
		if (title === MATERIAL_TITLE_NAME.FABRIC) {
			return (
				<>
					<p className="regular-12 gray_dark_03">{objectInfo?.fabricType}</p>
					<p className="regular-14 gray_dark_01 mb-2">{objectInfo?.construction}</p>
					{objectInfo?.compositionPartList?.map((compositionPart, index) => {
						return (
							<p className="regular-14 gray_dark_01 text-nowrap" key={`composition_${index}`}>
								{compositionPart?.percentage} {'% '}
								{compositionPart?.fiberName?.toProperCase()}
							</p>
						);
					})}
					<p className="regular-14 gray_dark_01 mt-2">
						{objectInfo?.gsm} {objectInfo?.weightUnit}
					</p>
				</>
			);
		} else if ([MATERIAL_TITLE_NAME.TRIMS, MATERIAL_TITLE_NAME.BRANDING].includes(title)) {
			return (
				<>
					<p className="weight-500 mb-2 gray_dark_01">
						{objectInfo?.subCategoryType ? objectInfo?.subCategoryType?.name : ''}
					</p>
					<p className="regular-14 gray_dark_01 mt-2">
						{categoryTypeSustainabilityForTrimsAndBranding(objectInfo)}
					</p>
					{someDetailsForTrimsAndBrandingRender(objectInfo)}
				</>
			);
		}
	};

	const renderMaterialColor = (color) => {
		if (color.colorType === 'SOLID' && color.representedBy === 'PANTONE_OR_HEX_CODE') {
			return (
				<Tooltip title={color?.name} placement="top" arrow key={color.id}>
					<div className="single-row d-flex">
						<span className="color-icon" style={{ background: color.hexCode }} />
						<span className="regular-14">{color.code}</span>
					</div>
				</Tooltip>
			);
		} else {
			return (
				<div className="single-row d-flex">
					<span className="regular-14">{color.name}</span>
				</div>
			);
		}
	};

	const getUnitTypes = (unit) => {
		return allUnitTypes?.map((item) => {
			if (unit === item.value) return item.label;
		});
	};

	const conditionWiseBookedColumnAdd = (item) => {
		if (item?.bookedQuantity !== undefined && item?.issueInventoryStatus === STATUS.BOOKED) {
			return (
				<td>
					<p>
						{item?.bookedQuantity}&nbsp;
						{getConsumptionUnit(item)}
					</p>
				</td>
			);
		} else if (item?.bookedQuantity !== undefined && item?.issueInventoryStatus !== STATUS.BOOKED) {
			return (
				<td>
					<>
						{item?.issueInventoryStatus === STATUS.CANCELLED && (
							<p className="regular-14 mb-2 red"> Booking cancel</p>
						)}
						{item?.issueInventoryStatus === STATUS.ACTIVE && (
							<p className="regular-14 mb-2 green">
								Issued: {item?.issuedQuantity} &nbsp;
								{getConsumptionUnit(item)}
							</p>
						)}
						<p
							className={
								'regular-14 gray_dark_01 ' +
								(item?.issueInventoryStatus === STATUS.CANCELLED ? 'red' : '')
							}
						>
							{item?.bookedQuantity}&nbsp;
							{getConsumptionUnit(item)}
						</p>
					</>
				</td>
			);
		} else {
			return <td />;
		}
	};

	const productMaterialItemRender = (item, titleName) => {
		let objectInfo = null;
		if (MATERIAL_TITLE_NAME.FABRIC === titleName) objectInfo = item.fabricDetailsTableResponse;
		else if ([MATERIAL_TITLE_NAME.TRIMS, MATERIAL_TITLE_NAME.BRANDING].includes(titleName))
			objectInfo = item.trimsAndBrandingTableResponse;
		return (
			<tr key={item.id}>
				<td>
					<img
						src={addImageSuffix(item.documentPath, '_xicon')}
						alt=""
						className="cell-img pr-2"
						onError={(e) => {
							e.target.onerror = null;
							e.target.src = `${process.env.PUBLIC_URL}/images/fabric-default.png`;
						}}
					/>
				</td>
				<td>
					{[MATERIAL_TITLE_NAME.TRIMS, MATERIAL_TITLE_NAME.BRANDING].includes(titleName) && (
						<p className="regular-14 gray_dark_01 mb-2">
							{objectInfo?.subCategoryType?.category}
						</p>
					)}

					{objectInfo?.placement ? (
						<span className="type-tag">
							{capitalizeFirstLetter(
								returnMaterialType(objectInfo?.placement)?.replaceAll('_', ' ')
							)}
						</span>
					) : (
						''
					)}
				</td>
				<td>
					<span className="regular-14 gray_dark_01">
						<p>{item.referenceNumber}</p>
						{['MAIN_FABRIC', 'EXTRA_FABRIC'].includes(item.materialType) && (
							<p>{item.isInventoryAvailable ? '' : '(Not in inventory)'}</p>
						)}
					</span>
				</td>
				<td>{materialDetailsRender(titleName, objectInfo)}</td>
				<td>
					<div className="color-column">
						<div className="color-list">
							{item.colorResponseList?.map((color, index) => (
								<React.Fragment key={`material_color_${index}`}>
									<p className={'gray_dark_01 mb-1'}>{color?.name}</p>
									{renderMaterialColor(color)}
								</React.Fragment>
							))}
						</div>
					</div>
				</td>
				{![MATERIAL_TITLE_NAME.TRIMS, MATERIAL_TITLE_NAME.BRANDING].includes(titleName) &&
					conditionWiseBookedColumnAdd(item, item.productMaterialIndexNumber)}
				<td className="consumption-column">
					<span className="regular-14 gray_dark_01">
						{item.quantity}&nbsp;
						{getUnitTypes(item.quantityUnit)}
					</span>
				</td>
				<td>
					<span className="regular-14 gray_dark_01">
						<p>{item?.supplierRefNumber}</p>
						<p className="regular-12 gray_dark_02 mt-1">{item?.supplierName}</p>
					</span>
				</td>
				<td>
					<span className="regular-14 gray_dark_01">{item.description}</span>
				</td>
			</tr>
		);
	};

	const productMaterialsTableRender = (list, titleName) => {
		if (list !== undefined && list.length > 0) {
			return (
				<>
					<p className="semibold-12 gray_dark_02 mt-3 mb-2">{titleName}</p>
					<div className="material-list-table">
						<div className="update-material-list-table table-responsive">
							<table className="table">
								<thead>
									<tr>
										<th>Image</th>
										<th>
											{[
												MATERIAL_TITLE_NAME.TRIMS,
												MATERIAL_TITLE_NAME.BRANDING
											].includes(titleName)
												? 'Material type & '
												: ''}
											Placement
										</th>
										<th>Reference</th>
										<th>Material details</th>
										<th>Color</th>
										{![MATERIAL_TITLE_NAME.TRIMS, MATERIAL_TITLE_NAME.BRANDING].includes(
											titleName
										) && <th>Nitex inventory book Qty</th>}
										<th className="consumption">Sample consumption</th>
										<th>Supplier</th>
										<th>Remarks</th>
									</tr>
								</thead>
								<tbody>
									{list?.map((item) => productMaterialItemRender(item, titleName))}
								</tbody>
							</table>
						</div>
					</div>
				</>
			);
		} else return '';
	};

	const renderMaterials = () => {
		return Object.keys(productMaterials)?.map((item, index) => {
			let productMaterialsTypeObj = productMaterials[item];
			let productInfo = products[item];
			if (productMaterialsTypeObj) {
				return (
					<div className="single-design-view-page" key={`materials_${index}`}>
						<div className="single-artboard-title">
							<p className="regular-16 weight-500 gray_dark_01 m-0">
								MATERIAL LIST ({productInfo?.name})
							</p>
						</div>
						<div className="full-body">
							{Object.keys(productMaterialsTypeObj)?.length === 3 &&
								productMaterialsTableRender(
									productMaterialsTypeObj?.fabric,
									MATERIAL_TITLE_NAME.FABRIC
								)}
							{Object.keys(productMaterialsTypeObj)?.length === 3 &&
								productMaterialsTableRender(
									productMaterialsTypeObj?.trims,
									MATERIAL_TITLE_NAME.TRIMS
								)}
							{Object.keys(productMaterialsTypeObj)?.length === 3 &&
								productMaterialsTableRender(
									productMaterialsTypeObj?.branding,
									MATERIAL_TITLE_NAME.BRANDING
								)}
						</div>
					</div>
				);
			}
		});
	};

	return Object.keys(productMaterials)?.length > 0 ? renderMaterials() : <></>;
};

export default TechPackMaterialSection;
